import type {
  ApiNodeDataExternalApi,
  HttpMethod,
  NodeDataUpdateExternalApi,
  VendorIntegration,
} from '@onefootprint/request-types/dashboard';
import type { ApplyEditMutation } from '../../base-node';
import type { VendorIntegrationFormData } from '../types';
import { decodeFormKey, getIntegrationInputNodes, getLeafFields, inputsToBodyJson } from './integration-schema';

// `requireSuccessStatusCode` is flattened out of the generated `ExternalApiConfig`. This drawer has no
// control for it, so carry the node's existing value through instead of resetting it on save.
type RawConfig = { requireSuccessStatusCode?: boolean };

export const createOnSubmitHandler = (
  node: { id: string; data: ApiNodeDataExternalApi },
  mutation: ApplyEditMutation,
  integrations: VendorIntegration[],
  onSubmit?: (data: NodeDataUpdateExternalApi) => void, // For storybook testing
) => {
  return async (data: VendorIntegrationFormData) => {
    const selectedApi = integrations.flatMap(i => i.apis).find(a => a.kind === data.config.integrationKind);
    const inputLeaves = getLeafFields(getIntegrationInputNodes(selectedApi?.inputSchema));
    const mockLeaves = getLeafFields(getIntegrationInputNodes(selectedApi?.outputSchema));

    const nodeData = {
      kind: 'external_api' as const,
      config: {
        headers: {},
        queryParams: {},
        method: 'post' as HttpMethod,
        body: inputsToBodyJson(inputLeaves, data.config.integrationInputs),
        requireSuccessStatusCode: (node.data.config as unknown as RawConfig).requireSuccessStatusCode ?? true,
        responseContentType: 'json',
        workflowDataAssignments: Object.entries(data.config.outputMappings ?? {})
          .filter(([, key]) => typeof key === 'string' && key.trim().length > 0)
          .map(([encodedPath, key]) => ({
            key: key.trim(),
            rule: { jsonPath: { selector: `$.${decodeFormKey(encodedPath)}` } },
          })),
      },
      // The drawer has no name field: an API's display name is already vendor-qualified
      // ("Sumsub Applicant Fetch"), so it names the node.
      name: selectedApi?.name ?? '',
      source: {
        vendorIntegration: {
          kind: data.config.integrationKind,
          ...(data.config.useIntegrationSandboxMock && {
            useMockData: inputsToBodyJson(mockLeaves, data.config.integrationMockData),
          }),
        },
      },
    };

    try {
      await mutation.mutateAsync({ kind: 'update_node', nodeData, nodeId: node.id });
      onSubmit?.(nodeData);
    } catch (error) {
      console.error('Error updating playbook session:', error);
    }
  };
};
